/**
 * Short-lived, same-browser post-auth intent (ato-api#267, RFC #261 §4).
 *
 * "Save to My Apps" and "Continue this session" are SEPARATE user intents —
 * merely signing in must NOT sweep every previewed app into My Apps. This
 * module carries the ONE explicit intent a user chose across an OAuth
 * round-trip via `sessionStorage` (NO server `auth_intents` table): after
 * sign-in completes, the callback consumes the intent and runs exactly that
 * action, and nothing else. A plain sign-in (no stored intent) runs neither.
 *
 * Only `save_app` is handled here; `continue_preview` is carried by the
 * existing session-continuation channel (`sessionContinuationChannel.ts`),
 * and the two are deliberately kept as distinct mechanisms so one can never
 * silently trigger the other.
 */

const SAVE_APP_KEY = "ato_post_auth_save_app";

/** An intent older than this is abandoned — the user started a save, never
 *  completed sign-in, and a LATER unrelated plain sign-in in the same tab must
 *  NOT resurrect it (Reviewer, Major). Matches the repo's `paidIntent.ts`
 *  15-minute staleness window for the same round-trip-intent pattern. */
const STALE_AFTER_MS = 15 * 60 * 1000;

/** A capsule source ref, e.g. `community/hello-capsule` — the SAME shape the
 *  library client and store use. Bounded + shape-checked so a stale/garbage
 *  value can never drive a save. */
function isValidSource(source: unknown): source is string {
  return (
    typeof source === "string" &&
    source.length > 0 &&
    source.length <= 200 &&
    // publisher/slug (or a bare slug) — never a URL, token, or path traversal.
    /^[a-z0-9][a-z0-9/_-]*$/i.test(source) &&
    !source.includes("//") &&
    !source.includes("..")
  );
}

/** Record "after sign-in, save exactly this capsule to My Apps". Timestamped so
 *  a stale, abandoned intent can't fire on a later unrelated sign-in. */
export function setSaveAppIntent(source: string): void {
  if (!isValidSource(source)) return;
  try {
    sessionStorage.setItem(
      SAVE_APP_KEY,
      JSON.stringify({ source, ts: Date.now() }),
    );
  } catch {
    // Private-mode / storage-disabled: the save simply won't auto-run
    // post-auth; the user can save again once signed in. Never throws.
  }
}

/** Read AND clear the save-app intent (single-use). Returns the capsule source
 *  to save, or null if there is no valid, FRESH pending intent (a stale one —
 *  older than STALE_AFTER_MS — is consumed and ignored, never saved). */
export function consumeSaveAppIntent(): string | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(SAVE_APP_KEY);
    sessionStorage.removeItem(SAVE_APP_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  let parsed: { source?: unknown; ts?: unknown };
  try {
    parsed = JSON.parse(raw) as { source?: unknown; ts?: unknown };
  } catch {
    return null;
  }
  const fresh =
    typeof parsed.ts === "number" && Date.now() - parsed.ts < STALE_AFTER_MS;
  if (!fresh) return null;
  return isValidSource(parsed.source) ? parsed.source : null;
}

/** Clear any pending save-app intent without consuming its value — used when a
 *  DIFFERENT intent (e.g. continue-preview) is chosen, so the two can never
 *  both fire from one sign-in. */
export function clearSaveAppIntent(): void {
  try {
    sessionStorage.removeItem(SAVE_APP_KEY);
  } catch {
    // no-op
  }
}
